import { FC } from "react";
import {
    CheckmarkCircleIcon,
    InvisalignLogoIcon,
} from "@smile2impress/components";
import {
    Box,
    useBreakpointValue,
    Flex,
    Heading,
    Text,
    Icon,
} from "@smile2impress/components/relaunch";
import { Awards } from "templates/relaunch/Awards";

import {
    TrustpilotWidget,
    TrustpilotWidgetVariant,
} from "components/TrustpilotWidget";

type MainBannerExtraBaseProps = {
    title: string;
    advantages: string[];

    disclaimer?: string;
};

export type MainBannerExtraProps = MainBannerExtraBaseProps &
    (
        | { trustpilotLink: string; invisalign?: false | never }
        | { trustpilotLink?: never; invisalign: true }
    );

export const MainBannerExtra: FC<MainBannerExtraProps> = ({
    title,
    advantages,
    disclaimer = "",
    trustpilotLink = "",
    invisalign = false,
}) => {
    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });

    return (
        <Flex
            direction="column"
            bgColor="base.grey"
            px={[2, 2, 4]}
            pt={[3, 3, 4]}
            pb={[2, 2, 4]}
            rowGap={[2, 2, 4]}
        >
            <Flex
                direction={["column", "column", "row"]}
                justifyContent="space-between"
                columnGap={3}
                rowGap={2}
            >
                <Flex direction="column" grow={1} basis={0}>
                    <Heading
                        textStyle="H2"
                        maxW={["100%", "100%", "105%"]}
                        color="base.gunmetal"
                        dangerouslySetInnerHTML={{
                            __html: title,
                        }}
                        sx={{
                            "&>*": {
                                color: "base.halfBlack",
                            },
                        }}
                    />
                    {trustpilotLink && (
                        <Box mt={2} display={["none", "none", "block"]}>
                            <TrustpilotWidget
                                link={trustpilotLink}
                                variant={
                                    TrustpilotWidgetVariant.MICRO_TRUST_SCORE
                                }
                            />
                        </Box>
                    )}
                    {invisalign && (
                        <Box mt={2} mb={["16px", "16px", "0"]}>
                            <Icon
                                as={InvisalignLogoIcon}
                                w={["125px", "125px", "156px"]}
                                h={["32px", "32px", "40px"]}
                            />
                        </Box>
                    )}
                </Flex>
                <Flex
                    direction="column"
                    grow={1}
                    basis={0}
                    rowGap={[0.5, 0.5, 1]}
                >
                    {advantages.map((adv, index) => (
                        <Flex
                            direction="row"
                            key={`adv${index}`}
                            columnGap={[0.7]}
                            alignItems="center"
                        >
                            <Icon
                                as={CheckmarkCircleIcon}
                                color="base.bitterSweet"
                                w={["24px", "24px", 3]}
                                h={["24px", "24px", 3]}
                            />
                            <Text
                                textStyle="title3"
                                sx={{
                                    "&>*": {
                                        color: "base.halfBlack",
                                    },
                                }}
                            >
                                {adv}
                            </Text>
                        </Flex>
                    ))}
                    {disclaimer && (
                        <Box mt={["6px"]}>
                            <Text
                                textStyle={["captionSmall"]}
                                color="base.halfBlack"
                            >
                                {disclaimer}
                            </Text>
                        </Box>
                    )}
                </Flex>
            </Flex>
            <Box
                px={[0, 0, 3]}
                pt={trustpilotLink ? 1 : 2}
                pb={2}
                bgColor="base.white"
                borderRadius="sm"
            >
                {trustpilotLink && (
                    <Box px={2} display={["block", "block", "none"]}>
                        <TrustpilotWidget
                            link={trustpilotLink}
                            variant={TrustpilotWidgetVariant.MICRO_TRUST_SCORE}
                        />
                        <Box
                            mx={2}
                            mt={1}
                            mb={1}
                            h="1px"
                            bgColor="rgba(8, 8, 8, 0.3)"
                        ></Box>
                    </Box>
                )}

                <Flex
                    direction="row"
                    height={["auto", "auto", "105px"]}
                    alignItems="center"
                >
                    <Awards noPX runningLine={isMobile} />
                </Flex>
            </Box>
        </Flex>
    );
};
